import type { Item } from "@/shared/components/generic/items";
import type { ModuleSubscription } from "../modules/ModuleSubscription";
import type { DomainPreferences } from "./DomainPreferences";
import type { SitePreferences } from "./SitePreferences";
import type { MyCawanModule } from "@/shared/model/modules/MyCawanModule";
import { isEmpty } from "@/shared/utils/Strings.ts";

export class Domain {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly shortName: string,
    public readonly preferences: DomainPreferences,
    public readonly sites: Site[],
  ) {}

  hasModule(module: MyCawanModule): boolean {
    return this.sites.some((s) => s.hasModule(module));
  }

  findSite(siteId: string | undefined): Site | undefined {
    return this.sites.find((s) => s.id === siteId);
  }

  favoriteSites(): Site[] {
    return this.sites.filter((s) => s.isFavorite);
  }
}

export class Site {
  constructor(
    public readonly domainId: string,
    public readonly id: string,
    public readonly name: string,
    public readonly code: string,
    public readonly isFavorite: boolean,
    public readonly subscriptions: ModuleSubscription[],
    public readonly preferences: SitePreferences,
  ) {}

  hasModule(module: MyCawanModule): boolean {
    return this.subscriptions.some(
      (sub) => sub.module === module && sub.isActive(),
    );
  }
}

export function domainsAsItems(domains: Domain[]): Item[] {
  return domains.map(domainAsItem);
}

export function domainAsItem(d: Domain): Item {
  return { key: d.id, label: d.name };
}

export function sitesAsItems(
  domains: Domain[],
  domainId?: string,
  displaySitesCodes?: boolean,
): Item[] {
  const d = findDomain(domains, domainId);
  if (!d) return [];
  return d.sites.map((s) => siteAsItem(s, displaySitesCodes));
}

export function siteAsItem(site: Site, displaySitesCodes?: boolean): Item {
  return {
    key: site.id,
    label: siteLabel(site, displaySitesCodes),
  };
}

export function siteLabel(site: Site, displaySitesCodes?: boolean): string {
  return (
      displaySitesCodes != undefined && displaySitesCodes && !isEmpty(site.code)
    ) ?
      `${site.code} - ${site.name}`
    : site.name;
}

export function domainSitesAsItems(
  domain: Domain,
  module: MyCawanModule,
  displaySitesCodes?: boolean,
): Item[] {
  return domain.sites
    .filter((s) => s.hasModule(module))
    .map((s) => siteAsItem(s, displaySitesCodes));
}

export function findDomain(
  domains: Domain[],
  domainId: string | undefined,
): Domain | undefined {
  return domains.find((d) => d.id === domainId);
}

export function sortSitesByPreference(
  sites: Site[],
  sortSitesByCode: boolean,
): Site[] {
  return [...sites].sort((s1, s2) => {
    if (sortSitesByCode) {
      if (isEmpty(s1.code) && !isEmpty(s2.code)) return -1;
      if (!isEmpty(s1.code) && isEmpty(s2.code)) return 1;

      const s1SiteNameWithCode = `${s1.code} ${s1.name}`;
      const s2SiteNameWithCode = `${s2.code} ${s2.name}`;
      return s1SiteNameWithCode.localeCompare(s2SiteNameWithCode);
    }
    return s1.name.localeCompare(s2.name);
  });
}
